<!--*
 * Header for sub-menus
 *
 * @author   pablo.barco
 * @date     2020-06-30
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <nav
    v-if="!hideHeaderBar"
    class="d-flex justify-content-between align-items-center sticky-top"
  >
    <HamburguerMenu
      :is-open="menuIsOpen"
      @onClose="toggleMenu"
      @showVideo="showVideo"
    />
    <div class="navbar-item py-0" style="width: 64px">
      <app-button
        v-if="showBackButton"
        btn-type="ghost"
        @click="goBack"
        layout="icon"
        icon="arrow-left"
      />
    </div>
    <div ref="title" class="title-header navbar-item h4 p-0 m-0">
      {{ title }}
    </div>
    <div class="d-flex flex-wrap align-items-center">
      <smtt-dropdown
        class="d-none d--md-inline mr-3"
        :name="$t('burgerMenu.language')"
        icon="language"
        btn-type="outline"
      >
        <smtt-dropdown-item
          :key="language.key"
          v-for="language in languages"
          @click="changeLanguage(language.key)"
        >
          <b v-if="language.key === currentLocale">{{ language.value }}</b>
          <span v-else>{{ language.value }}</span>
        </smtt-dropdown-item>
      </smtt-dropdown>
      <div class="d-none d--md-inline-block user-info mx-3">
        <b style="font-size: 16px">{{ userFullName }}</b
        ><br />
        <span style="font-size: 14px">{{ userMainRole }}</span>
      </div>
      <app-button
        class="ml-3"
        btn-type="ghost"
        @click="toggleMenu"
        layout="icon"
        :icon="menuIcon"
      />
    </div>
  </nav>
</template>

<script>
import store from "../store/store";
import { languageNames } from "@/i18n/main";
import { mapActions } from "vuex";
import AppButton from "@/components/AppButton";

import HamburguerMenu from "./hamburgerMenu/HamburguerMenu";

export default {
  name: "SubMenuHeader",
  props: {
    title: {
      type: String,
      default: "Smart Field",
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    customGoBack: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: () => {
    return {
      hideHeaderBar: false,
      menuIsOpen: false,
    };
  },
  components: { HamburguerMenu, AppButton },
  computed: {
    menuIcon() {
      return this.menuIsOpen ? "angle-righ-double" : "burger-menu";
    },
    userFullName() {
      return (
        this.$store.state.loggedUser?.firstname +
        " " +
        this.$store.state.loggedUser?.lastname
      );
    },
    userMainRole() {
      return this.$store.state.loggedUser?.roles[0]?.description ?? "";
    },
    currentLocale() {
      return this.$i18n.locale();
    },
    languages() {
      return languageNames;
    },
  },
  methods: {
    ...mapActions(["changeLocale"]),
    toggleMenu() {
      this.menuIsOpen = !this.menuIsOpen;
    },
    goBack() {
      if (this.customGoBack) {
        this.$emit("onGoBack");
      } else {
        this.$router.back();
      }
    },
    showVideo() {
      this.$emit("showVideo");
    },
    changeLanguage(language) {
      this.$i18n.set(language);
      this.changeLocale({ locale: language });
    },
    handleResize() {
      const headerBar = this.$refs.headerBar;
      if (headerBar) {
        const headerBarWidth = headerBar.clientWidth;
        let maxTextWidth;

        // Calculate the width of the header bar text to display
        if (headerBarWidth < 753) {
          maxTextWidth = headerBarWidth - 200;
        } else {
          const extendedControls = this.$refs.extendedControls;
          maxTextWidth = headerBarWidth - 150 - extendedControls.clientWidth;
        }

        const titleElement = this.$refs.title;
        if (titleElement) {
          titleElement.style.width = `${maxTextWidth}px`;
        }
      }
    },
  },
  created() {
    this.hideHeaderBar = store.getters.HideHeaderBar();
  },
};
</script>

<style scoped>
nav {
  height: 52px;
  transition: all 0.5s;
  background-color: #f5f5f5;
  border-bottom: rgba(224, 222, 222, 0.99) 1px solid;
  overflow: visible;
  z-index: 50;
}

.navbar-item {
  z-index: 10000;
  display: block;
}

.user-info {
  line-height: 1.2;
}

.title-header {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: center;
}
</style>
